/** Cross-feature API for durable upload processing. */
import {
  createExtractionJobBatchApiExtractionHomesHomeIdJobsBatchPost,
  createExtractionJobApiExtractionHomesHomeIdJobsPost,
  completeExtractionJobApiExtractionHomesHomeIdJobsJobIdCompletePost,
  dismissExtractionJobApiExtractionHomesHomeIdJobsJobIdDismissPost,
  getExtractionJobApiExtractionHomesHomeIdJobsJobIdGet,
  listExtractionJobsApiExtractionHomesHomeIdJobsGet,
  retryExtractionJobApiExtractionHomesHomeIdJobsJobIdRetryPost,
} from '@/client';
import type {
  ExtractionBatchResponse,
  ExtractionJobResponse,
  ExtractionKind,
  FieldPatch,
} from '@/client/types.gen';
import { createApiError } from '@/lib/api-error';
import { httpClient } from '@/lib/http-client';

export const extractionJobKeys = {
  all: (homeId: string) => ['extractionJobs', homeId] as const,
};

export const EXTRACTION_MAX_FILES_PER_BATCH = 10;
export const EXTRACTION_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const EXTRACTION_MAX_BATCH_BYTES = 100 * 1024 * 1024;

export type ExtractionUploadLimitError = 'too_many_files' | 'file_too_large' | 'batch_too_large';

export function getExtractionUploadLimitError(files: File[]): ExtractionUploadLimitError | null {
  if (files.length > EXTRACTION_MAX_FILES_PER_BATCH) return 'too_many_files';
  if (files.some((file) => file.size > EXTRACTION_MAX_FILE_BYTES)) return 'file_too_large';
  if (files.reduce((total, file) => total + file.size, 0) > EXTRACTION_MAX_BATCH_BYTES) {
    return 'batch_too_large';
  }
  return null;
}

const idempotencyKey = () => crypto.randomUUID();

export async function createExtractionJob(
  homeId: string,
  file: File,
  kind?: ExtractionKind,
): Promise<ExtractionJobResponse> {
  const response = await createExtractionJobApiExtractionHomesHomeIdJobsPost({
    client: httpClient,
    path: { home_id: homeId },
    body: { file, ...(kind ? { kind } : {}) },
    headers: { authorization: '', 'Idempotency-Key': idempotencyKey() },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data as ExtractionJobResponse;
}

export async function createExtractionJobs(
  homeId: string,
  files: File[],
  kind?: ExtractionKind,
): Promise<ExtractionBatchResponse> {
  if (!files.length) return { items: [] };
  if (getExtractionUploadLimitError(files)) {
    throw new RangeError('Extraction upload batch exceeds the supported limits');
  }
  if (files.length === 1) {
    const job = await createExtractionJob(homeId, files[0], kind);
    return { items: [{ filename: files[0].name, job }] };
  }
  const response = await createExtractionJobBatchApiExtractionHomesHomeIdJobsBatchPost({
    client: httpClient,
    path: { home_id: homeId },
    body: { files, ...(kind ? { kind } : {}) },
    headers: { authorization: '', 'Idempotency-Key': idempotencyKey() },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data as ExtractionBatchResponse;
}

export async function listExtractionJobs(homeId: string): Promise<ExtractionJobResponse[]> {
  const response = await listExtractionJobsApiExtractionHomesHomeIdJobsGet({
    client: httpClient,
    path: { home_id: homeId },
    query: { limit: 100 },
    headers: { authorization: '' },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data ?? [];
}

export async function getExtractionJob(
  homeId: string,
  jobId: string,
): Promise<ExtractionJobResponse> {
  const response = await getExtractionJobApiExtractionHomesHomeIdJobsJobIdGet({
    client: httpClient,
    path: { home_id: homeId, job_id: jobId },
    headers: { authorization: '' },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data as ExtractionJobResponse;
}

export async function completeExtractionJob(
  homeId: string,
  jobId: string,
  targetType: 'expense' | 'document' | 'booking',
  targetId: string,
  patch: FieldPatch[] = [],
): Promise<ExtractionJobResponse> {
  const response = await completeExtractionJobApiExtractionHomesHomeIdJobsJobIdCompletePost({
    client: httpClient,
    path: { home_id: homeId, job_id: jobId },
    body: { target_type: targetType, target_id: targetId, patch },
    headers: { authorization: '' },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data as ExtractionJobResponse;
}

export async function dismissExtractionJob(
  homeId: string,
  jobId: string,
): Promise<ExtractionJobResponse> {
  const response = await dismissExtractionJobApiExtractionHomesHomeIdJobsJobIdDismissPost({
    client: httpClient,
    path: { home_id: homeId, job_id: jobId },
    headers: { authorization: '' },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data as ExtractionJobResponse;
}

export async function retryExtractionJob(
  homeId: string,
  jobId: string,
): Promise<ExtractionJobResponse> {
  const response = await retryExtractionJobApiExtractionHomesHomeIdJobsJobIdRetryPost({
    client: httpClient,
    path: { home_id: homeId, job_id: jobId },
    headers: { authorization: '' },
    throwOnError: false,
  });
  if (response.error) throw createApiError(response.error);
  return response.data as ExtractionJobResponse;
}
